import { useLocalization } from '@ais-ui/locale-management';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@ais-ui/ui-core';
import { cn, CursorPositionFormat, DateTimeFormat, Units } from '@ais-ui/utils';
import { UseFormReturn } from 'react-hook-form';
import { GeneralFormValues } from './useGeneralForm';

interface GeneralFormProps {
  form: UseFormReturn<GeneralFormValues>;
  getTimeZonesPlusLocalUserTimeZone: () => string[];
}

export function GeneralForm({
  form,
  getTimeZonesPlusLocalUserTimeZone,
}: GeneralFormProps) {
  const { messages } = useLocalization();

  return (
    <Form {...form}>
      <div className="space-y-12">
        {/* Formatting Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">
            {messages.settingsGeneralFormattingTitle}
          </h3>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="dateFormat"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4'
                  )}
                >
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsGeneralDateFormatLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsGeneralDateFormatDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger
                        id="date-format-dropdown-button"
                        className="w-[240px]"
                      >
                        <SelectValue placeholder="Select a date time format" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(DateTimeFormat).map(([key, format]) => (
                          <SelectItem
                            key={format}
                            value={format}
                            id={`date-format-${key.toLowerCase()}`}
                          >
                            {format}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="timeZone"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4'
                  )}
                >
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsGeneralTimezoneLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsGeneralTimezoneDescription}
                    </FormDescription>
                  </div>
                  <FormControl onChange={() => form.clearErrors()}>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger
                        id="time-zone-dropdown-button"
                        className="w-[240px]"
                      >
                        <SelectValue placeholder="Select a time zone" />
                      </SelectTrigger>
                      <SelectContent>
                        {getTimeZonesPlusLocalUserTimeZone().map((timeZone) => (
                          <SelectItem
                            key={timeZone}
                            value={timeZone}
                            id={`time-zone-${timeZone.toLowerCase()}`}
                          >
                            {timeZone}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="showCursorPosition"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsGeneralEnableCursorPositionLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsGeneralEnableCursorPositionDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      id="show-cursor-position-switch"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="cursorPositionFormat"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4'
                  )}
                >
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsGeneralPositionFormatLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsGeneralPositionFormatDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger
                        id="cursor-position-format-dropdown-button"
                        className="w-[240px]"
                      >
                        <SelectValue placeholder="Select cursor position format" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(CursorPositionFormat).map((format) => (
                          <SelectItem
                            key={format}
                            value={format}
                            id={`cursor-position-format-${format.toLowerCase()}`}
                          >
                            {format}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Units Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">
            {messages.settingsGeneralUnitsTitle}
          </h3>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="lengthUnit"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4'
                  )}
                >
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsGeneralUnitsLength}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsGeneralUnitsLengthDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger
                        id="unit-length-dropdown-button"
                        className="w-[240px]"
                      >
                        <SelectValue placeholder="Select a unit system" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          id="Metric"
                          key={Units.METRIC}
                          value={Units.METRIC}
                        >
                          Metric (km, m, cm)
                        </SelectItem>
                        <SelectItem
                          id="Imperial"
                          key={Units.IMPERIAL}
                          value={Units.IMPERIAL}
                        >
                          Imperial (mi, ft, in)
                        </SelectItem>
                        <SelectItem
                          id="Maritime"
                          key={Units.MARITIME}
                          value={Units.MARITIME}
                        >
                          Maritime (NM, m)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Layout Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Layout</h3>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="isSidebarExpanded"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4'
                  )}
                >
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsGeneralLayoutSidebarExpandedLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsGeneralLayoutSidebarExpandedDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      id="sidebar-expanded-layout-switch"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tableDensity"
              render={({ field }) => (
                <FormItem className="space-y-3 border rounded-lg p-4">
                  <FormLabel className="text-base">Table Density</FormLabel>
                  <FormDescription>
                    Choose how compact rows in the table should appear.
                  </FormDescription>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value ?? 'normal'}
                      className="flex space-x-4"
                    >
                      <FormItem className="flex items-baseline space-x-2">
                        <FormControl>
                          <RadioGroupItem value="normal" />
                        </FormControl>
                        <FormLabel className="font-normal">Normal</FormLabel>
                      </FormItem>
                      <FormItem className="flex items-baseline space-x-2">
                        <FormControl>
                          <RadioGroupItem value="compact" />
                        </FormControl>
                        <FormLabel className="font-normal">Compact</FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Remote Control Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Remote control</h3>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="isRemoteControlEnabled"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4'
                  )}
                >
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {
                        messages.settingsGeneralRemoteControlEnabledMessagesLabel
                      }
                    </FormLabel>
                    <FormDescription>
                      {
                        messages.settingsGeneralRemoteControlEnabledMessagesDescription
                      }
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      id="remote-control-enabled-switch"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Map Scale Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Map scale</h3>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="isMapScaleEnabled"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4'
                  )}
                >
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsGeneralMapScaleEnabledLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsGeneralMapScaleEnabledDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      id="map-scale-enabled-switch"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Zoom Buttons Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Zoom buttons</h3>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="areZoomButtonsEnabled"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-center justify-between border rounded-lg p-4'
                  )}
                >
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      {messages.settingsGeneralZoomButtonsEnabledLabel}
                    </FormLabel>
                    <FormDescription>
                      {messages.settingsGeneralZoomButtonsEnabledDescription}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      id="zoom-buttons-enabled-switch"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
    </Form>
  );
}
